import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
} from "axios";
import { useAuthStore } from "@/stores/authStore";
import type { RefreshTokenResponse } from "@/types/auth";

const BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

export const api: AxiosInstance = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

type QueueItem = {
  resolve: (token: string) => void;
  reject: (error: AxiosError) => void;
};

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

const processQueue = (
  error: AxiosError | null,
  token: string | null = null,
): void => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error || token === null) {
      reject(error as AxiosError);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = useAuthStore.getState().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        })
        .catch((err: unknown) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const { refreshToken, setTokens, logout } = useAuthStore.getState();

    if (!refreshToken) {
      logout();
      window.location.replace("/login");
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post<RefreshTokenResponse>(
        `${BASE_URL}/api/v1/auth/refresh`,
        { refreshToken },
        { headers: { "Content-Type": "application/json" } },
      );

      const { accessToken: newAccess, refreshToken: newRefresh } =
        data.data.tokens;
      setTokens(newAccess, newRefresh ?? refreshToken);
      processQueue(null, newAccess);

      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError as AxiosError, null);

      // FIX(Day 83): Call server logout to delete the stale refresh token from DB.
      // Without this, logout() only clears Zustand/localStorage — the DB token
      // survives and blocks the next login() with "already logged in on another device".
      axios
        .post(
          `${BASE_URL}/api/v1/auth/logout`,
          { refreshToken },
          { headers: { "Content-Type": "application/json" } },
        )
        .catch(() => {}); // fire-and-forget — never block the redirect

      logout();
      window.location.replace("/login");
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;

// Day 83 fix: clear stale DB token on refresh failure to unblock next login
